import React from "react";
import { Box, TextField, Button } from "@mui/material";
import { FormControl, Select, MenuItem } from "@mui/material";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ProceduresTable from "./ProceduresTable";
import SelectField from "./SelectField";
import LabeledTextField from "./LabeledTextField";
import SectionHeader from "./SectionHeader";
import { sdmTableStyles, getHeaderCellStyle } from "./sdmTableStyles";

const RATING_OPTIONS = ["отл", "хор", "удовл", "неуд", "плох"];
const RISK_OPTIONS = ["низкие", "приемл", "повыш", "высокие"];

const ApplicationTab = ({ data, onUpdate, companyIndex = 0 }) => {
  const updateField = (path, value) => {
    onUpdate(path, value);
  };

  // Helper function to convert ISO date to YYYY-MM-DD format for input
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    // If it's ISO format (YYYY-MM-DDTHH:mm:ss)
    if (dateString.includes('T')) {
      return dateString.split('T')[0];
    }
    return dateString;
  };

  // Get current company data from companiesInfo
  const currentCompany = data.generalInfo?.companiesInfo?.[companyIndex] || {};

  // Calculate totals for bank limits
  const calculateActiveTotal = () => {
    const bankLimit = data.application?.bankLimit || {};
    const items = [bankLimit.credits, bankLimit.overdrafts, bankLimit.leasing, bankLimit.factoring, bankLimit.guarantees, bankLimit.individuals];
    return items.reduce((sum, item) => {
      return sum + (parseFloat(item?.active) || 0);
    }, 0).toFixed(1);
  };

  const calculateDebtTotal = () => {
    const bankLimit = data.application?.bankLimit || {};
    const items = [bankLimit.credits, bankLimit.overdrafts, bankLimit.leasing, bankLimit.factoring, bankLimit.guarantees, bankLimit.individuals];
    return items.reduce((sum, item) => {
      return sum + (parseFloat(item?.debt) || 0);
    }, 0).toFixed(1);
  };

  const handleAddCreditRequest = () => {
    const newRequest = {
      companyName: "",
      product: "",
      amount: "",
      currency: "",
      rate: "",
      term: "",
      termUnit: "мес",
      collateral: ""
    };
    const currentRequests = data.application?.creditRequest || [];
    updateField("application.creditRequest", [...currentRequests, newRequest]);
  };

  const handleDeleteCreditRequest = (index) => {
    const currentRequests = data.application?.creditRequest || [];
    const updatedRequests = currentRequests.filter((_, i) => i !== index);
    updateField("application.creditRequest", updatedRequests);
  };

  return (
    <Box>
      {/* Section: Application */}
      <Box sx={{ marginBottom: "32px" }}>
        <SectionHeader>Заявка</SectionHeader>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", marginBottom: "16px" }}>
          <Box>
            <Box sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: "#1A1E24",
              marginBottom: "8px"
            }}>
              Кредитный Комитет
            </Box>
            <TextField
              type="date"
              value={formatDateForInput(data.application?.creditCommittee)}
              onChange={(e) => {
                if (e.target.value) {
                  updateField("application.creditCommittee", `${e.target.value}T00:00:00`);
                } else {
                  updateField("application.creditCommittee", null);
                }
              }}
              size="small"
              variant="outlined"
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "14px",
                },
              }}
            />
          </Box>

          <Box>
            <Box sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: "#1A1E24",
              marginBottom: "8px"
            }}>
              дата последнего рассмотрения на КК:
            </Box>
            <TextField
              type="date"
              value={formatDateForInput(data.application?.lastCreditCommitteeDate)}
              onChange={(e) => {
                if (e.target.value) {
                  updateField("application.lastCreditCommitteeDate", `${e.target.value}T00:00:00`);
                } else {
                  updateField("application.lastCreditCommitteeDate", null);
                }
              }}
              size="small"
              variant="outlined"
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "14px",
                },
              }}
            />
          </Box>
        </Box>

        {/* Bank Limits Table Section */}
        <Box sx={{ marginTop: "24px", marginBottom: "24px", display: "flex", gap: "24px" }}>
          {/* Table */}
          <Box sx={{ flex: 1 }}>
            <TableContainer component={Paper} elevation={0} sx={sdmTableStyles.tableContainer}>
              <Table size={sdmTableStyles.tableSize}>
                <TableHead>
                  <TableRow sx={sdmTableStyles.headerRow}>
                    <TableCell sx={getHeaderCellStyle(150)}>Лимит Банка в млн. рублей:</TableCell>
                    <TableCell sx={getHeaderCellStyle(150)}>действует {calculateActiveTotal()}</TableCell>
                    <TableCell sx={getHeaderCellStyle(150)}>задолженн. {calculateDebtTotal()}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    { label: "кредиты", key: "credits" },
                    { label: "овердрафты", key: "overdrafts" },
                    { label: "лизинг", key: "leasing" },
                    { label: "факторинг", key: "factoring" },
                    { label: "гарантии", key: "guarantees" },
                    { label: "физ.лица", key: "individuals" }
                  ].map((row, index) => (
                    <TableRow key={index} sx={sdmTableStyles.dataRow}>
                      <TableCell sx={getHeaderCellStyle(150)}>{row.label}</TableCell>
                      <TableCell sx={sdmTableStyles.dataCell}>
                        <TextField
                          type="number"
                          value={data.application?.bankLimit?.[row.key]?.active || ""}
                          onChange={(e) => updateField(`application.bankLimit.${row.key}.active`, e.target.value)}
                          onWheel={(e) => e.target.blur()}
                          size="small"
                          variant="outlined"
                          fullWidth
                          sx={sdmTableStyles.numberField}
                        />
                      </TableCell>
                      <TableCell sx={sdmTableStyles.dataCell}>
                        <TextField
                          type="number"
                          value={data.application?.bankLimit?.[row.key]?.debt || ""}
                          onChange={(e) => updateField(`application.bankLimit.${row.key}.debt`, e.target.value)}
                          onWheel={(e) => e.target.blur()}
                          size="small"
                          variant="outlined"
                          fullWidth
                          sx={sdmTableStyles.numberField}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Right Side Fields */}
          <Box sx={{ flex: 0, minWidth: "200px" }}>
            <Box sx={{ marginBottom: "16px" }}>
              <Box sx={{
                fontSize: "12px",
                fontWeight: 600,
                textTransform: "uppercase",
                color: "#64748B",
                marginBottom: "8px"
              }}>
                макс. лимит
              </Box>
              <TextField
                value={data.application?.bankLimit?.maxLimit || ""}
                onChange={(e) => updateField("application.bankLimit.maxLimit", e.target.value)}
                type="number"
                onWheel={(e) => e.target.blur()}
                size="small"
                variant="outlined"
                fullWidth
                sx={sdmTableStyles.numberField}
              />
            </Box>

            <Box>
              <Box sx={{
                fontSize: "12px",
                fontWeight: 600,
                textTransform: "uppercase",
                color: "#64748B",
                marginBottom: "8px",
                lineHeight: "1.3"
              }}>
                Кр.история в СДМ<br />
              </Box>
              <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <Box sx={{ fontSize: "12px", fontWeight: 600, color: "#64748B", paddingTop: "4px" }}>с </Box>
                <TextField
                  placeholder="Год"
                  value={data.application?.bankLimit?.creditHistorySince || ""}
                  onChange={(e) => {
                    updateField("application.bankLimit.creditHistorySince", e.target.value);
                  }}
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  size="small"
                  variant="outlined"
                  sx={{
                    flex: 1,
                    ...sdmTableStyles.numberField
                  }}
                />
                <Box sx={{ fontSize: "12px", fontWeight: 600, color: "#64748B", paddingTop: "4px" }}>г.</Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ marginTop: "24px", marginBottom: "24px" }}>
          <Box sx={{
            fontSize: "12px",
            fontWeight: 600,
            textTransform: "uppercase",
            color: "#64748B",
            marginBottom: "8px"
          }}>
            Суть бизнеса заемщика:
          </Box>
          <TextField
            value={data.application?.businessEssence || ""}
            onChange={(e) => updateField("application.businessEssence", e.target.value)}
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            sx={sdmTableStyles.textFieldMultiline}
          />
        </Box>

        {/* Application Essence and Amount Section */}
        <Box sx={{ marginBottom: "24px", marginTop: "24px" }}>
          <Box sx={{
            display: "flex",
            gap: "12px",
            alignItems: "center"
          }}>
            <Box sx={{
              fontSize: "12px",
              fontWeight: 600,
              textTransform: "uppercase",
              color: "#64748B",
              minWidth: "200px",
              flexShrink: 0,
              paddingTop: "4px"
            }}>
              Сумма и суть заявки клиента:
            </Box>

            <FormControl size="small" sx={{ flex: 1, minWidth: "200px" }}>
              <Select
                value={data.application?.applicationEssence || ""}
                onChange={(e) => updateField("application.applicationEssence", e.target.value)}
                displayEmpty
                sx={sdmTableStyles.selectField}
              >
                <MenuItem value="">
                  <em>Не выбрано</em>
                </MenuItem>
                <MenuItem value="в рамках лимита">в рамках лимита</MenuItem>
                <MenuItem value="с увеличением лимита на">с увеличением лимита на</MenuItem>
                <MenuItem value="со снижением лимита на">со снижением лимита на</MenuItem>
              </Select>
            </FormControl>

            <TextField
              placeholder="Сумма"
              value={data.application?.applicationAmount || ""}
              onChange={(e) => updateField("application.applicationAmount", e.target.value)}
              type="number"
              onWheel={(e) => e.target.blur()}
              size="small"
              variant="outlined"
              sx={{
                flex: 0,
                minWidth: "100px",
                ...sdmTableStyles.numberField
              }}
            />

            <Box sx={{ flex: 0, fontSize: "12px", fontWeight: 600, color: "#64748B", paddingTop: "4px" }}>
              млн.
            </Box>

            <FormControl size="small" sx={{ flex: 0, minWidth: "100px" }}>
              <Select
                value={data.application?.applicationCurrency || ""}
                onChange={(e) => updateField("application.applicationCurrency", e.target.value)}
                displayEmpty
                sx={sdmTableStyles.selectField}
              >
                <MenuItem value="">
                  <em>Валюта</em>
                </MenuItem>
                <MenuItem value="РУБ">РУБ</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="EUR">EUR</MenuItem>
                <MenuItem value="CNY">CNY</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Credit Requests Table */}
        <Box sx={sdmTableStyles.tableWrapper}>
          <Box sx={sdmTableStyles.headerSection}>
            <Box sx={sdmTableStyles.sectionTitle}>
              ЗАЯВКИ ПО КРЕДИТНЫМ ПРОДУКТАМ
            </Box>
          </Box>

          <TableContainer component={Paper} elevation={0} sx={sdmTableStyles.tableContainer}>
            <Table size={sdmTableStyles.tableSize}>
              <TableHead>
                <TableRow sx={sdmTableStyles.headerRow}>
                  <TableCell sx={getHeaderCellStyle(150)}>Заявка от</TableCell>
                  <TableCell sx={getHeaderCellStyle(150)}>Продукт</TableCell>
                  <TableCell sx={getHeaderCellStyle(110)}>Сумма</TableCell>
                  <TableCell sx={getHeaderCellStyle(80)}>Вал.</TableCell>
                  <TableCell sx={getHeaderCellStyle(70)}>%</TableCell>
                  <TableCell sx={getHeaderCellStyle(140)}>Срок</TableCell>
                  <TableCell sx={getHeaderCellStyle(150)}>Залог</TableCell>
                  <TableCell sx={sdmTableStyles.emptyCell} />
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.application?.creditRequest || []).map((request, index) => (
                  <TableRow key={index} sx={sdmTableStyles.dataRow}>
                    <TableCell sx={sdmTableStyles.dataCell}>
                      <TextField
                        placeholder="Компания"
                        value={request.requestFrom || ""}
                        onChange={(e) => updateField(`application.creditRequest.${index}.requestFrom`, e.target.value)}
                        size="small"
                        variant="outlined"
                        fullWidth
                        sx={sdmTableStyles.textField}
                      />
                    </TableCell>
                    <TableCell sx={sdmTableStyles.dataCell}>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={request.product || ""}
                          onChange={(e) => updateField(`application.creditRequest.${index}.product`, e.target.value)}
                          displayEmpty
                          sx={sdmTableStyles.selectField}
                        >
                          <MenuItem value=""><em>-</em></MenuItem>
                          <MenuItem value="Кредит">кредит</MenuItem>
                          <MenuItem value="Кредитная линия">кредитная линия</MenuItem>
                          <MenuItem value="Овердрафт">овердрафт</MenuItem>
                          <MenuItem value="Лизинг">лизинг</MenuItem>
                          <MenuItem value="Факторинг">факторинг</MenuItem>
                          <MenuItem value="Банковская гарантия">банковская гарантия</MenuItem>
                          <MenuItem value="Лимит банк.гарантий">лимит банк.гарантий</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell sx={sdmTableStyles.dataCell}>
                      <TextField
                        type="number"
                        value={request.amount || ""}
                        onChange={(e) => updateField(`application.creditRequest.${index}.amount`, e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        size="small"
                        variant="outlined"
                        fullWidth
                        sx={sdmTableStyles.numberField}
                      />
                    </TableCell>
                    <TableCell sx={sdmTableStyles.dataCell}>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={request.currency || ""}
                          onChange={(e) => updateField(`application.creditRequest.${index}.currency`, e.target.value)}
                          displayEmpty
                          sx={sdmTableStyles.selectField}
                        >
                          <MenuItem value=""><em>-</em></MenuItem>
                          <MenuItem value="RUB">РУБ</MenuItem>
                          <MenuItem value="USD">USD</MenuItem>
                          <MenuItem value="EUR">EUR</MenuItem>
                          <MenuItem value="CNY">CNY</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell sx={sdmTableStyles.dataCell}>
                      <TextField
                        type="number"
                        value={request.rate || ""}
                        onChange={(e) => updateField(`application.creditRequest.${index}.rate`, e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        size="small"
                        variant="outlined"
                        fullWidth
                        sx={sdmTableStyles.numberField}
                      />
                    </TableCell>
                    <TableCell sx={sdmTableStyles.dataCell}>
                      <Box sx={{ display: "flex", gap: "4px" }}>
                        <TextField
                          type="number"
                          placeholder="Срок"
                          value={request.term || ""}
                          onChange={(e) => updateField(`application.creditRequest.${index}.term`, e.target.value)}
                          onWheel={(e) => e.target.blur()}
                          size="small"
                          variant="outlined"
                          sx={{ ...sdmTableStyles.numberField, flex: 1 }}
                        />
                        <FormControl size="small" sx={{ minWidth: "60px" }}>
                          <Select
                            value={request.termUnit || "мес"}
                            onChange={(e) => updateField(`application.creditRequest.${index}.termUnit`, e.target.value)}
                            sx={sdmTableStyles.selectField}
                          >
                            <MenuItem value="год">год</MenuItem>
                            <MenuItem value="мес">мес</MenuItem>
                            <MenuItem value="дн">дн</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    </TableCell>
                    <TableCell sx={sdmTableStyles.dataCell}>
                      <TextField
                        placeholder="Залог"
                        value={request.collateral || ""}
                        onChange={(e) => updateField(`application.creditRequest.${index}.collateral`, e.target.value)}
                        size="small"
                        variant="outlined"
                        fullWidth
                        sx={sdmTableStyles.textField}
                      />
                    </TableCell>
                    <TableCell sx={sdmTableStyles.dataCell}>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteCreditRequest(index)}
                        sx={sdmTableStyles.deleteButton}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={8} sx={sdmTableStyles.addRowCell}>
                    <Button
                      fullWidth
                      onClick={handleAddCreditRequest}
                      startIcon={<AddCircleIcon />}
                      sx={sdmTableStyles.addRowButton}
                    >
                      Добавить строку
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Comments Field */}
        <Box sx={{ marginBottom: "24px", marginTop: "24px" }}>
          <Box sx={{
            fontSize: "15px",
            fontWeight: 600,
            color: "#111827",
            letterSpacing: "-0.01em",
            marginBottom: "12px"
          }}>
            Комментарии (цель кредита, подробнее о залогах, поручит-ва, график погашения и пр.)
          </Box>
          <TextField
            value={data.application?.comments || ""}
            onChange={(e) => updateField("application.comments", e.target.value)}
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            sx={sdmTableStyles.textFieldMultiline}
          />
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", marginTop: "32px", marginBottom: "24px" }}>
          {/* Left Column */}
          <Box>
            <SelectField
              label="Положение отрасли"
              value={currentCompany?.industryCondition?.selector || ""}
              onChange={(val) => updateField(`generalInfo.companiesInfo.${companyIndex}.industryCondition.selector`, val)}
              options={RATING_OPTIONS}
            />

            <SelectField
              label="Положение клиента в отрасли"
              value={currentCompany?.clientPositionInIndustry?.selector || ""}
              onChange={(val) => updateField(`generalInfo.companiesInfo.${companyIndex}.clientPositionInIndustry.selector`, val)}
              options={RATING_OPTIONS}
            />

            <SelectField
              label="Прозрачность бизнеса"
              value={currentCompany?.businessTransparency?.selector || ""}
              onChange={(val) => updateField(`generalInfo.companiesInfo.${companyIndex}.businessTransparency.selector`, val)}
              options={RATING_OPTIONS}
            />

            <SelectField
              label="Репутация клиента, рекомендации"
              value={currentCompany?.clientReputationAndReferences?.selector || ""}
              onChange={(val) => updateField(`generalInfo.companiesInfo.${companyIndex}.clientReputationAndReferences.selector`, val)}
              options={RATING_OPTIONS}
            />

            <SelectField
              label="Качество менеджмента"
              value={currentCompany?.managementQuality?.selector || ""}
              onChange={(val) => updateField(`generalInfo.companiesInfo.${companyIndex}.managementQuality.selector`, val)}
              options={RATING_OPTIONS}
            />

            <SelectField
              label="Динамика продаж"
              value={data.application?.salesDynamics || ""}
              onChange={(val) => updateField("application.salesDynamics", val)}
              options={RATING_OPTIONS}
            />

            <SelectField
              label="Качество и диверс.клиент.базы"
              value={data.application?.customerBaseQualityAndDiversification || ""}
              onChange={(val) => updateField("application.customerBaseQualityAndDiversification", val)}
              options={RATING_OPTIONS}
            />
          </Box>

          {/* Right Column */}
          <Box>
            <SelectField
              label="Финансовая устойчивость"
              value={data.application?.financialStability || ""}
              onChange={(val) => updateField("application.financialStability", val)}
              options={RATING_OPTIONS}
            />

            <SelectField
              label="Ликвидность и платежесп."
              value={data.application?.liquidityAndPaymentDiscipline || ""}
              onChange={(val) => updateField("application.liquidityAndPaymentDiscipline", val)}
              options={RATING_OPTIONS}
            />

            <SelectField
              label="Прибыльность бизнеса"
              value={data.application?.businessProfitability || ""}
              onChange={(val) => updateField("application.businessProfitability", val)}
              options={RATING_OPTIONS}
            />

            <SelectField
              label="Качество залога и его достаточность:"
              value={currentCompany?.collateralQualityAndSufficiency?.selector || ""}
              onChange={(val) => updateField(`generalInfo.companiesInfo.${companyIndex}.collateralQualityAndSufficiency.selector`, val)}
              options={RATING_OPTIONS}
            />

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", marginTop: "24px" }}>
              <Box>
                <SelectField
                  label="Экологич.риски"
                  value={data.application?.environmentalRisks || ""}
                  onChange={(val) => updateField("application.environmentalRisks", val)}
                  options={RISK_OPTIONS}
                />
              </Box>

              <Box>
                <SelectField
                  label="Социальн.риски"
                  value={data.application?.socialRisks || ""}
                  onChange={(val) => updateField("application.socialRisks", val)}
                  options={RISK_OPTIONS}
                />
              </Box>
            </Box>

            <SelectField
              label="Риски бизнеса клиента:"
              value={currentCompany?.businessRisksOfClient?.selector || ""}
              onChange={(val) => updateField(`generalInfo.companiesInfo.${companyIndex}.businessRisksOfClient.selector`, val)}
              options={RISK_OPTIONS}
            />

            <SelectField
              label="Риски кредитной сделки:"
              value={currentCompany?.risksOfCreditTransaction?.selector || ""}
              onChange={(val) => updateField(`generalInfo.companiesInfo.${companyIndex}.risksOfCreditTransaction.selector`, val)}
              options={RISK_OPTIONS}
            />
          </Box>
        </Box>

        <Box sx={{ marginTop: "32px" }}>
          <ProceduresTable
            data={data.application?.procedures || {}}
            onChange={(newData) => updateField("application.procedures", newData)}
          />
        </Box>

        {/* Signature Fields */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "24px", marginTop: "32px", marginBottom: "24px" }}>
          <LabeledTextField
            label="Подготовил"
            value={data.application?.preparedBy || ""}
            onChange={(val) => updateField("application.preparedBy", val)}
          />

          <LabeledTextField
            label="Проверил"
            value={data.application?.checkedBy || ""}
            onChange={(val) => updateField("application.checkedBy", val)}
          />

          <LabeledTextField
            label="Эксперт по КК"
            value={data.application?.creditCommitteeExpert || ""}
            onChange={(val) => updateField("application.creditCommitteeExpert", val)}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ApplicationTab;
